/**
 * The Rainvic design language as React components: one signal colour, Lucide
 * icons, 12 px smallest text, every control reaches 44 px on touch. Ported
 * from the design-system bundle; the CSS is in components.css.
 */
import { useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { Icon, type IconName } from './icon.js';

export { Icon, ICON_NAMES, type IconName } from './icon.js';
export const cx = (...parts: Array<string | false | null | undefined>): string => parts.filter(Boolean).join(' ');

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> { variant?: 'primary' | 'secondary' | 'ghost' | 'danger'; size?: 'sm' | 'md' | 'lg'; icon?: IconName; busy?: boolean; }
export function Button({ variant = 'secondary', size = 'md', icon, busy, children, className, disabled, type = 'button', ...rest }: ButtonProps) {
  return (
    <button type={type} {...rest} disabled={disabled || busy} aria-busy={busy || undefined} className={cx('rv-btn', `rv-btn--${variant}`, `rv-btn--${size}`, className)}>
      {busy ? <Icon name="loader" size={size === 'sm' ? 14 : 16} className="rv-spin" /> : icon ? <Icon name={icon} size={size === 'sm' ? 14 : 16} /> : null}
      {children != null ? <span>{children}</span> : null}
    </button>
  );
}

export function IconButton({ icon, label, size = 'md', active, className, type = 'button', ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { icon: IconName; label: string; size?: 'sm' | 'md'; active?: boolean }) {
  return (
    <button type={type} {...rest} aria-label={label} title={label} aria-pressed={active == null ? undefined : !!active} className={cx('rv-iconbtn', `rv-iconbtn--${size}`, active && 'is-active', className)}>
      <Icon name={icon} size={size === 'sm' ? 14 : 16} />
    </button>
  );
}

type FieldBase = { label: string; hint?: string; error?: string; className?: string; id?: string };
export type TextFieldProps = FieldBase & ({ multiline?: false } & InputHTMLAttributes<HTMLInputElement> | { multiline: true } & TextareaHTMLAttributes<HTMLTextAreaElement>);
export function TextField(p: TextFieldProps) {
  const { label, hint, error, className, id: givenId, multiline, ...rest } = p as FieldBase & { multiline?: boolean } & Record<string, unknown>;
  const id = givenId ?? `rv-f-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  const described = error ? `${id}-err` : hint ? `${id}-hint` : undefined;
  const common = { id, 'aria-invalid': error ? true : undefined, 'aria-describedby': described, className: 'rv-field__input' };
  return (
    <div className={cx('rv-field', error && 'is-error', className)}>
      <label htmlFor={id} className="rv-field__label">{label}</label>
      {multiline ? <textarea {...(rest as TextareaHTMLAttributes<HTMLTextAreaElement>)} {...common} rows={3} /> : <input {...(rest as InputHTMLAttributes<HTMLInputElement>)} {...common} />}
      {error ? <p id={`${id}-err`} className="rv-field__msg rv-field__msg--error" role="alert">{error}</p> : hint ? <p id={`${id}-hint`} className="rv-field__msg">{hint}</p> : null}
    </div>
  );
}

const STATUS: Record<string, { label: string; icon: IconName; tone: string }> = {
  in_review: { label: 'In review', icon: 'eye', tone: 'neutral' },
  changes_requested: { label: 'Changes requested', icon: 'message-square', tone: 'warning' },
  approved: { label: 'Approved', icon: 'circle-check', tone: 'approved' },
  released: { label: 'Released', icon: 'upload', tone: 'released' },
  released_not_youtube: { label: 'Released elsewhere', icon: 'globe', tone: 'released' },
  active: { label: 'Active', icon: 'circle-check', tone: 'approved' },
  past_due: { label: 'Payment due', icon: 'triangle-alert', tone: 'warning' },
  cancelled: { label: 'Cancelled', icon: 'x', tone: 'neutral' },
  trial_expired: { label: 'Trial ended', icon: 'x', tone: 'neutral' },
};
export function StatusChip({ status, className }: { status: string; className?: string }) {
  const s = STATUS[status] ?? { label: status, icon: 'eye' as IconName, tone: 'neutral' };
  return <span className={cx('rv-chip', `rv-chip--${s.tone}`, className)}><Icon name={s.icon} size={12} /><span>{s.label}</span></span>;
}

export interface SegmentedOption { id: string; label: string; icon?: IconName; iconOnly?: boolean; }
export function Segmented({ options, value, defaultValue, onChange, label, className }: { options: SegmentedOption[]; value?: string; defaultValue?: string; onChange?: (id: string) => void; label: string; className?: string }) {
  const [inner, setInner] = useState(defaultValue ?? options[0]?.id ?? '');
  const val = value ?? inner;
  return (
    <div role="radiogroup" aria-label={label} className={cx('rv-seg', className)}>
      {options.map((o) => { const on = o.id === val; return (
        <button key={o.id} type="button" role="radio" aria-checked={on} title={o.iconOnly ? o.label : undefined} aria-label={o.iconOnly ? o.label : undefined} className={cx('rv-seg__opt', on && 'is-on')} onClick={() => { setInner(o.id); onChange?.(o.id); }}>
          {o.icon ? <Icon name={o.icon} size={14} /> : null}{o.iconOnly ? null : <span>{o.label}</span>}
        </button>); })}
    </div>
  );
}

export interface NavItem { id: string; label: string; icon: IconName; badge?: number; }
export function AppNav({ items, active, onSelect, layout = 'sidebar', brand, label = 'Main', className }: { items: NavItem[]; active: string; onSelect: (id: string) => void; layout?: 'sidebar' | 'rail' | 'tabs'; brand?: string; label?: string; className?: string }) {
  const shown = layout === 'tabs' && items.length > 5 ? [...items.slice(0, 4), { id: '__more', label: 'More', icon: 'ellipsis' as IconName }] : items;
  return (
    <nav aria-label={label} className={cx('rv-nav', `rv-nav--${layout}`, className)}>
      {layout === 'sidebar' && brand ? <div className="rv-nav__brand"><Icon name="sparkles" size={16} /><span>{brand}</span></div> : null}
      <ul className="rv-nav__list">
        {shown.map((it) => { const on = it.id === active; return (
          <li key={it.id}>
            <a href={`/${it.id}`} className={cx('rv-nav__item', on && 'is-active')} aria-current={on ? 'page' : undefined} title={layout === 'rail' ? it.label : undefined} onClick={(e) => { e.preventDefault(); onSelect(it.id); }}>
              <span className="rv-nav__icon"><Icon name={it.icon} size={layout === 'tabs' ? 20 : 16} />{it.badge ? <span className="rv-nav__badge" aria-label={`${it.badge} unread`}>{it.badge > 99 ? '99+' : it.badge}</span> : null}</span>
              <span className="rv-nav__label">{it.label}</span>
            </a>
          </li>); })}
      </ul>
    </nav>
  );
}

export function CreditMeter({ allowance, used, reserved = 0, resetLabel, className }: { allowance: number; used: number; reserved?: number; resetLabel: string; className?: string }) {
  const u = Math.min(Math.max(0, used), allowance); const r = Math.min(Math.max(0, reserved), allowance - u);
  const left = Math.max(0, allowance - u - r); const share = allowance ? left / allowance : 0;
  const tone = left === 0 ? 'out' : share < 0.15 ? 'low' : 'ok';
  const pct = (n: number) => `${allowance ? (n / allowance) * 100 : 0}%`;
  return (
    <section className={cx('rv-credits', `is-${tone}`, className)} aria-label="Credits">
      <div className="rv-credits__top"><span className="rv-credits__num">{left.toLocaleString('en-US')}</span><span className="rv-credits__unit">credits left</span></div>
      <div className="rv-credits__bar" role="meter" aria-valuemin={0} aria-valuemax={allowance} aria-valuenow={left} aria-label={`${left} of ${allowance} credits left`}>
        <span className="rv-credits__used" style={{ width: pct(u) }} /><span className="rv-credits__reserved" style={{ width: pct(r) }} />
      </div>
      <p className="rv-credits__foot">{tone === 'out' ? `Metered actions are paused. Buy a pack or wait for ${resetLabel}.` : `${r ? `${r.toLocaleString('en-US')} held by running jobs · ` : ''}Resets ${resetLabel}`}</p>
    </section>
  );
}

/** A short line under a page title or above a form. Helper lines stay under 12 words. */
export function Note({ tone = 'muted', children }: { tone?: 'muted' | 'danger' | 'warning'; children: ReactNode }) {
  return <p className={cx('rv-note', `rv-note--${tone}`)} role={tone === 'danger' ? 'alert' : undefined}>{children}</p>;
}
